import { SELECTED_CURATORS_KEY } from '@/constants/state'
import { useMixpanel } from '@/context/mixpanel'
import { useVaultsSearchState } from '@/hooks/useVaultsSearchState'
import type { Vault } from '@/types/vault'
import { getCuratorDisplayData } from '@/utils/curator'
import type { PopoverProps } from '@radix-ui/react-popover'
import { getCuratorImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { AccordionItem } from '../common/accordion'
import { RoundImage } from '../common/roundImage'
import { FilterAccordionContent, FilterAccordionTrigger, FilterSelect } from './filterSelect'
import { FilterListContent } from './filterSelectContent'

interface Props {
  vaults: Vault[]
  className?: string
}

function useCuratorFilterData(vaults: Vault[]) {
  const { selectedCurators, updateSearchState } = useVaultsSearchState()
  const curators = useMemo(() => {
    const names = vaults
      .map((vault) => vault.curator?.name)
      .flatMap((name) => (name?.includes('/') ? name.split('/') : name))
    return [...new Set(names.filter((n): n is string => Boolean(n)))].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    )
  }, [vaults])
  const curatorDisplayData = useMemo(
    () =>
      Object.fromEntries(
        vaults.flatMap((vault) => {
          if (!vault.curator) return []

          const { entries, displayName, canMapDisplayNames } = getCuratorDisplayData(vault.curator)
          return entries.map(({ name, displayName: itemDisplayName }) => [
            name.toLowerCase(),
            {
              displayName: itemDisplayName,
              searchDisplayName: canMapDisplayNames ? itemDisplayName : displayName,
            },
          ])
        })
      ),
    [vaults]
  )
  const { trackEvent } = useMixpanel()

  return {
    items: curators,
    selectedItems: selectedCurators,
    setSelectedItems: (value: string[]) => {
      updateSearchState([SELECTED_CURATORS_KEY, value])
      trackEvent('Filter used', { Filter: 'curator', Value: value.join(', ') })
    },
    name: 'curator',
    renderItemPrefix: (item: string) => (
      <RoundImage
        src={getCuratorImage(item)}
        fallback={getCuratorImage('fallback')}
        alt={`${curatorDisplayData[item.toLowerCase()]?.displayName ?? item} curator`}
        className="size-7"
      />
    ),
    itemFormatter: (item: string) => curatorDisplayData[item.toLowerCase()]?.displayName ?? item,
    searchNormalizer: (item: string) =>
      `${item} ${curatorDisplayData[item.toLowerCase()]?.searchDisplayName.toLowerCase() ?? ''}`,
  }
}

export function CuratorFilter({ vaults, className, modal }: Props & Pick<PopoverProps, 'modal'>) {
  const props = useCuratorFilterData(vaults)
  return <FilterSelect {...props} scrollClassName="max-h-56" modal={modal} className={className} />
}

export function CuratorFilterAccordion({ vaults }: Props) {
  const props = useCuratorFilterData(vaults)
  return (
    <AccordionItem value="curator">
      <FilterAccordionTrigger name="curator" />
      <FilterAccordionContent>
        <FilterListContent {...props} scrollClassName="max-h-48" />
      </FilterAccordionContent>
    </AccordionItem>
  )
}
